import { useRef, useEffect, type ReactNode } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Trash2, Plus, X } from 'lucide-react'
import styled, { css } from 'styled-components'
import { SPACING, TYPOGRAPHY, FONT_WEIGHT } from '../../../../styles/theme'
import {
  Card,
  TopRow,
  DragHandle,
  IndexBadge,
  QuestionTextArea,
  DeleteButton,
} from '../../../../components/QuestionCardChrome/QuestionCardChrome.styles'
import type { SurveyQuestion, SurveyQuestionType } from '@bandos/shared'

// ─── Props ────────────────────────────────────────────────────────────────────

export interface QuestionEditorProps {
  id: string
  question: SurveyQuestion
  index: number
  onChange: (q: SurveyQuestion) => void
  onDelete: () => void
  /** Optional side-note shown above the editor body (e.g. source attribution). */
  attribution?: ReactNode
}

// ─── Styled (survey-specific) ─────────────────────────────────────────────────

const TypeRow = styled.div`
  display: flex;
  gap: ${SPACING.xs};
  flex-wrap: wrap;
  padding-left: 38px;
`

const typePillSelected = css`
  background: var(--color-primary-bg);
  border-color: var(--color-primary);
  color: var(--color-primary);
`

const TypePill = styled.button<{ $selected: boolean }>`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  padding: 3px 10px;
  border-radius: 20px;
  border: 1px solid var(--color-border-dark);
  background: var(--color-bg-subtle, rgba(0, 0, 0, 0.03));
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
  }

  ${({ $selected }) => $selected && typePillSelected}
`

const OptionsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs};
  padding-left: 38px;
`

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.xs};
`

const OptionDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--color-text-tertiary);
  flex-shrink: 0;
`

const OptionInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  border-bottom: 1px solid var(--color-border);
  border-radius: 0;
  padding: 3px 0;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-family: inherit;
  color: var(--color-text-primary);
  outline: none;
  transition: border-color 0.15s ease;

  &:focus {
    border-bottom-color: var(--color-primary);
  }

  &::placeholder {
    color: var(--color-text-disabled);
  }
`

const RemoveOptionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  color: var(--color-text-tertiary);
  cursor: pointer;
  border-radius: 4px;
  padding: 0;
  flex-shrink: 0;
  transition: color 0.15s ease;

  &:hover {
    color: #ef4444;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`

const AddOptionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: none;
  color: var(--color-primary);
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  cursor: pointer;
  padding: ${SPACING.xs} 0;
  margin-left: 14px;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.75;
  }
`

// ─── Type config ──────────────────────────────────────────────────────────────

const TYPE_OPTIONS: { value: SurveyQuestionType; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'scale', label: 'Scale 1–5' },
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'multiple_choice', label: 'Multiple Choice' },
  { value: 'checkbox', label: 'Checkbox' },
]

// ─── Component ────────────────────────────────────────────────────────────────

export function QuestionEditor({
  id,
  question,
  index,
  onChange,
  onDelete,
  attribution,
}: QuestionEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  })

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [question.question])

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange({ ...question, question: e.target.value })
  }

  const handleTypeChange = (newType: SurveyQuestionType) => {
    if (newType === question.type) return
    const updated: SurveyQuestion = { ...question, type: newType }
    if (newType === 'multiple_choice' || newType === 'checkbox') {
      updated.options = ['', '']
    } else {
      delete updated.options
    }
    onChange(updated)
  }

  const handleOptionChange = (optIndex: number, value: string) => {
    const newOptions = [...(question.options ?? [])]
    newOptions[optIndex] = value
    onChange({ ...question, options: newOptions })
  }

  const handleAddOption = () => {
    onChange({ ...question, options: [...(question.options ?? []), ''] })
  }

  const handleRemoveOption = (optIndex: number) => {
    const newOptions = (question.options ?? []).filter((_, i) => i !== optIndex)
    onChange({ ...question, options: newOptions })
  }

  return (
    <Card
      ref={setNodeRef}
      $isDragging={isDragging}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      {attribution}
      <TopRow>
        <DragHandle
          type="button"
          {...listeners}
          {...attributes}
          title="Drag to reorder"
          tabIndex={-1}
        >
          <GripVertical size={14} />
        </DragHandle>
        <IndexBadge>{index + 1}.</IndexBadge>
        <QuestionTextArea
          ref={textareaRef}
          value={question.question}
          onChange={handleTextChange}
          placeholder="Enter your question…"
          rows={1}
        />
        <DeleteButton onClick={onDelete} title="Delete question">
          <Trash2 size={14} />
        </DeleteButton>
      </TopRow>

      <TypeRow>
        {TYPE_OPTIONS.map((opt) => (
          <TypePill
            key={opt.value}
            type="button"
            $selected={question.type === opt.value}
            onClick={() => handleTypeChange(opt.value)}
          >
            {opt.label}
          </TypePill>
        ))}
      </TypeRow>

      {(question.type === 'multiple_choice' || question.type === 'checkbox') && (
        <OptionsSection>
          {(question.options ?? []).map((opt, i) => (
            <OptionRow key={i}>
              <OptionDot />
              <OptionInput
                value={opt}
                onChange={(e) => handleOptionChange(i, e.target.value)}
                placeholder={`Option ${i + 1}`}
              />
              <RemoveOptionButton
                type="button"
                onClick={() => handleRemoveOption(i)}
                disabled={(question.options ?? []).length <= 2}
                title="Remove option"
              >
                <X size={12} />
              </RemoveOptionButton>
            </OptionRow>
          ))}
          <AddOptionButton type="button" onClick={handleAddOption}>
            <Plus size={12} />
            Add option
          </AddOptionButton>
        </OptionsSection>
      )}
    </Card>
  )
}
